import { useCallback, useEffect, useRef, useState, type CSSProperties, type TouchEvent } from 'react';

export interface SwipeTabsOptions {
  /** Number of tabs and the index of the active one (-1 when the current page is not a tab). */
  count: number;
  index: number;
  /** Called with the neighbouring tab index once the swipe commits (after the page has slid out). */
  onChange: (index: number) => void;
}

export interface SwipeNeighbour {
  /** Tab index being revealed and the side it sits on (1 = right / next, -1 = left / previous). */
  index: number;
  dir: 1 | -1;
  /** Position for the preview pane: starts one screen to the side and follows the finger with the page. */
  style: CSSProperties;
}

const LOCK_PX = 8; // movement before the gesture is judged horizontal or vertical
const COMMIT_RATIO = 0.25; // of the pane width
const COMMIT_VELOCITY = 0.45; // px per ms
const EDGE_RESISTANCE = 0.3; // rubber band when there is no tab on that side
const OUT_MS = 180;
const SNAP_MS = 280;
const SNAP_EASE = 'cubic-bezier(.22,1,.36,1)';

/**
 * Touch-follow swipe between the bottom tabs on a phone. The page tracks the finger horizontally (vertical scrolling
 * stays native through `touch-action: pan-y`) while the neighbouring tab's page rides alongside it as a preview, so
 * what is coming is visible during the drag, not only after release. With no tab on that side the page resists with
 * a rubber band. On release it either slides the rest of the way and switches, or snaps back. Controls that use
 * horizontal movement themselves (sliders, inputs, anything that scrolls sideways, `data-no-swipe`) are left alone.
 * No effect with a mouse.
 */
export function useSwipeTabs({ count, index, onChange }: SwipeTabsOptions) {
  const ref = useRef<HTMLDivElement>(null);
  const [motion, setMotion] = useState<{ dx: number; transition: string; neighbour: { index: number; dir: 1 | -1 } | null }>({ dx: 0, transition: '', neighbour: null });
  const gesture = useRef<{ x: number; y: number; t: number; lock: 'h' | 'v' | null; dx: number; lastX: number; lastT: number; vx: number } | null>(null);
  const previewed = useRef(false);
  const busy = useRef(false);
  const width = () => ref.current?.clientWidth || window.innerWidth || 1;

  // After a committed swipe the new page is already where the preview was: drop the offset in one frame, no animation.
  useEffect(() => {
    if (!previewed.current) return;
    previewed.current = false;
    setMotion({ dx: 0, transition: '', neighbour: null });
    busy.current = false;
  }, [index]);

  const ignores = (target: EventTarget | null): boolean => {
    let el = target as HTMLElement | null;
    while (el && el !== ref.current) {
      if (el.matches?.('input, textarea, select, [role="slider"], [data-no-swipe]')) return true;
      const cs = getComputedStyle(el);
      if ((cs.overflowX === 'auto' || cs.overflowX === 'scroll') && el.scrollWidth > el.clientWidth + 1) return true;
      el = el.parentElement;
    }
    return false;
  };

  const onTouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    if (busy.current || e.touches.length !== 1 || ignores(e.target)) return;
    const t = e.touches[0]!;
    gesture.current = { x: t.clientX, y: t.clientY, t: performance.now(), lock: null, dx: 0, lastX: t.clientX, lastT: performance.now(), vx: 0 };
  }, []);

  const onTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      const g = gesture.current;
      if (!g || e.touches.length !== 1) return;
      const t = e.touches[0]!;
      const dx = t.clientX - g.x;
      const dy = t.clientY - g.y;
      if (g.lock === null) {
        if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;
        g.lock = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
      }
      if (g.lock !== 'h') return;
      const now = performance.now();
      const dt = now - g.lastT;
      if (dt > 0) g.vx = (t.clientX - g.lastX) / dt;
      g.lastX = t.clientX;
      g.lastT = now;
      const dir: 1 | -1 = dx < 0 ? 1 : -1; // swiping left reveals the next tab
      const target = index + dir;
      const hasNeighbour = index >= 0 && target >= 0 && target < count;
      g.dx = hasNeighbour ? dx : dx * EDGE_RESISTANCE;
      setMotion({ dx: g.dx, transition: 'none', neighbour: hasNeighbour ? { index: target, dir } : null });
    },
    [count, index],
  );

  const onTouchEnd = useCallback(() => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.lock !== 'h') return;
    const w = width();
    const dir: 1 | -1 = g.dx < 0 ? 1 : -1;
    const target = index + dir;
    const hasNeighbour = index >= 0 && target >= 0 && target < count;
    const commit = hasNeighbour && (Math.abs(g.dx) > w * COMMIT_RATIO || (Math.abs(g.vx) > COMMIT_VELOCITY && Math.sign(g.vx) === -dir));
    if (!commit) {
      setMotion((m) => ({ ...m, dx: 0, transition: `transform ${SNAP_MS}ms ${SNAP_EASE}` }));
      setTimeout(() => setMotion({ dx: 0, transition: '', neighbour: null }), SNAP_MS);
      return;
    }
    busy.current = true;
    setMotion({ dx: -dir * w, transition: `transform ${OUT_MS}ms ease-out`, neighbour: { index: target, dir } });
    setTimeout(() => {
      previewed.current = true; // the preview becomes the page: no second entrance
      onChange(target);
    }, OUT_MS);
  }, [count, index, onChange]);

  const moving = motion.dx !== 0 || motion.transition !== '';
  const style: CSSProperties = moving ? { transform: `translateX(${motion.dx}px)`, transition: motion.transition, touchAction: 'pan-y' } : { touchAction: 'pan-y' };
  const neighbour: SwipeNeighbour | null = motion.neighbour
    ? {
        ...motion.neighbour,
        style: { transform: `translateX(calc(${motion.neighbour.dir * 100}% + ${motion.dx}px))`, transition: motion.transition },
      }
    : null;

  return {
    ref,
    style,
    neighbour,
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd },
  };
}
